import { createBrowserRouter, createRoutesFromElements, RouterProvider, Route, Navigate } from "react-router";
import { lazy, Suspense } from "react";
import NavBarLayout from "./layouts/NavBarLayout";
import SignupPage from "./pages/Signup/SignupPage";
import LoginPage from "./pages/Login/LoginPage";
import OtpPage from "./pages/Login/OtpPage";
import StudentSignupPage from "./pages/Signup/StudentSignupPage";
import LecturerSignupPage from "./pages/Signup/LecturerSignupPage";
import Test from "./pages/Test";
import ProtectedRoute from "./components/ProtectedRoute";
import RootLayout from "./layouts/RootLayout";
import RequestResetPasswordPage from "./pages/ForgotPassword/RequestResetPasswordPage";
import ResetPasswordPage from "./pages/ForgotPassword/ResetPasswordPage";
import ApplyEnterprisePage from "./pages/Subscription/ApplyEnterprisePage";
import { Toaster } from "sonner";
import RoleSelectionPage from "./pages/Signup/RoleSelectionPage";
import TopicQuizPage from "./pages/Quiz/TopicQuizPage";

import TakeQuizPage from "./pages/Quiz/TakeQuizPage";
import StartQuizPage from "./pages/Quiz/StartQuizPage";
import QuizResultsPage from "./pages/Quiz/QuizResultsPage";
import { HelmetProvider } from "react-helmet-async";
import NotFound from "./pages/NotFound";
import PageLoader from "./components/PageLoader";
import AdminOrLecturerLayout from "./layouts/AdminOrLecturerLayout";
import QuizQuestionManagementPage from "./pages/Admin/Quiz/QuizQuestionManagementPage";
import QuizResponsePage from "./pages/Lecturer/QuizResponsePage";
import NotificationsPage from "./pages/Notifications/NotificationsPage";

// Lazy load route modules for code-splitting per dashboard
const AdminRoutes = lazy(() => import("./routes/AdminRoutes"));
const LecturerRoutes = lazy(() => import("./routes/LecturerRoutes"));
const StudentRoutes = lazy(() => import("./routes/StudentRoutes"));

const routes = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" element={<RootLayout />}>
      <Route index element={<Navigate to="/login" replace />} />
      <Route path="/test" element={<Test />} />
      <Route path="*" element={<NotFound />} />
      {/* pages with top navbar */}
      <Route element={<NavBarLayout />}>
        <Route path="login" element={<LoginPage />} handle={{ title: "Login | ExamPrep" }} />
        <Route path="verify-otp" element={<OtpPage />} handle={{ title: "Verify Email | ExamPrep" }} />
        <Route path="signup" element={<SignupPage />} handle={{ title: "Signup | ExamPrep" }} />
        <Route path="request-reset-password" element={<RequestResetPasswordPage />} />
        <Route path="reset-password/:userId/:token" element={<ResetPasswordPage />} />
        <Route path="apply-enterprise" element={<ApplyEnterprisePage />} handle={{ title: "Apply for Enterprise | ExamPrep" }} />


        <Route path="topics/:topic_id/quizzes" element={<TopicQuizPage />} />

        {/* protected routes */}
        <Route element={<ProtectedRoute />}>
          {/* profile completion routes */}
          <Route path="complete-profile">
            <Route index element={<RoleSelectionPage />} />
            <Route path="student" element={<StudentSignupPage />} />
            <Route path="lecturer" element={<LecturerSignupPage />} />
            <Route path="role-selection" element={<RoleSelectionPage />} />
          </Route>

          {/* page where users take quiz */}
          <Route path="quizzes/:quiz_id/view" element={<StartQuizPage />} />
          <Route path="quizzes/:quiz_id/take" element={<TakeQuizPage />} />
          <Route path="quizzes/:session_id/results" element={<QuizResultsPage />} />
          <Route path="notifications" element={<NotificationsPage />} handle={{ title: "Notifications | ExamPrep" }} />


          {/* Dashboard routes with code-splitting per dashboard */}
          <Route
            path="admin/*"
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminRoutes />
              </Suspense>
            }
          />

          <Route element={<ProtectedRoute requireUserType={true} allowedUserTypes={["lecturer"]} />}>
            <Route
              path="lecturer/*"
              element={
                <Suspense fallback={<PageLoader />}>
                  <LecturerRoutes />
                </Suspense>
              }
            />
          </Route>

          <Route element={<ProtectedRoute requireUserType={true} allowedUserTypes={["student", "preper"]} />}>
            <Route
              path="student/*"
              element={
                <Suspense fallback={<PageLoader />}>
                  <StudentRoutes />
                </Suspense>
              }
            />
            <Route
              path="preper/*"
              element={
                <Suspense fallback={<PageLoader />}>
                  <StudentRoutes />
                </Suspense>
              }
            />
          </Route>

          <Route path="manage/quizzes" element={<AdminOrLecturerLayout />}>
            <Route path=":quiz_id" element={<QuizQuestionManagementPage />} />
            <Route path=":quiz_id/responses" element={<QuizResponsePage />} />
          </Route>
        </Route>
      </Route>

      {/* pages without top navbar */}
    </Route>,
  ),
);

function App() {
  return (
    <HelmetProvider>
      <RouterProvider router={routes} />
      <Toaster position="top-center" richColors={true} />
    </HelmetProvider>
  );
}

export default App;
